import { useState, useEffect } from 'react';

/**
 * Classifies the current client device as mobile, tablet, or desktop.
 */
export enum DeviceType {
    mobile = "mobile",
    tablet = "tablet",
    desktop = "desktop",
    notSet = "notSet"
}

const maxMobileWidth = 768;
const maxTabletWidth = 1024;

/**
 * Detects whether the current client is a mobile, tablet, or desktop device, using either the browser's user agent string or (optionally) live viewport width, re-evaluating on window resize.
 *
 * @param useScreenSize - When `true`, classifies by viewport width thresholds first (falling back to user-agent sniffing); when `false` (default), relies purely on user-agent sniffing.
 * @returns The detected `DeviceType`, updated on resize when `useScreenSize` is `true`.
 */
export const useDeviceDetection = (useScreenSize = false) => {
    const [device, setDevice] = useState<DeviceType>(DeviceType.notSet);

    useEffect(() => {
        const handleDeviceDetection = () => {
            const userAgent = navigator.userAgent.toLowerCase();
            const isMobile = /iphone|ipod|android|blackberry|windows phone/g.test(userAgent);
            const isTablet = /(ipad|tablet|playbook|silk|ipad pro|ipad air|ipad mini)|(android(?!.*mobile))/i.test(userAgent);
            // const isTablet = /(ipad|tablet|playbook|silk)|(android(?!.*mobile))/g.test(userAgent);

            let detectedDevice: DeviceType = DeviceType.notSet;

            if (useScreenSize) {
                const screenWidth = window.innerWidth;
                const isMobileSize = screenWidth <= maxMobileWidth; // Adjust this threshold as needed
                const isTabletSize = screenWidth > maxMobileWidth && screenWidth <= maxTabletWidth; // Adjust this threshold as needed

                if (isMobileSize) {
                    detectedDevice = DeviceType.mobile;
                } else if (isTabletSize) {
                    detectedDevice = DeviceType.tablet;
                }
            }

            if (detectedDevice === DeviceType.notSet) {
                if (isMobile) {
                    detectedDevice = DeviceType.mobile;
                } else if (isTablet) {
                    detectedDevice = DeviceType.tablet;
                } else {
                    detectedDevice = DeviceType.desktop;
                }
            }

            setDevice(detectedDevice);
        };

        handleDeviceDetection();

        if (useScreenSize) {
            window.addEventListener('resize', handleDeviceDetection);
            return () => {
                window.removeEventListener('resize', handleDeviceDetection);
            };
        }
    }, [useScreenSize]);

    return device;
};


export default useDeviceDetection;
